/**
 * Art Set Mock Component
 *
 * Web component for rendering lifestyle mock images with multiple artwork frames.
 * Each frame position has polygon corners that define the perspective transform.
 *
 * Uses CSS matrix3d transforms calculated via homography for accurate perspective warping.
 * Supports mobile square crop transforms and lighting overlays.
 */

class ArtSetMock extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.isUpdating = false;
    this.imageBounds = { x: 0, y: 0, width: 0, height: 0 };
  }

  connectedCallback() {
    try {
      // Parse data attributes
      this.framePolygons = JSON.parse(this.dataset.framePolygons || '{}');
      this.artworks = JSON.parse(this.dataset.artworks || '[]');
      this.lightingOpacity = parseFloat(this.dataset.lightingOpacity) || 0;
      this.lightingBlend = this.dataset.lightingBlend || 'multiply';
      this.squareCrop = JSON.parse(this.dataset.squareCrop || 'null');
      this.imgAspect = parseFloat(this.dataset.imgAspect) || 1.6;
      this.isMobileCrop = this.hasAttribute('data-mobile-crop');

      // Get DOM references
      this.cropWrapper = this.querySelector('.art-set-mock__crop-wrapper');
      this.framesContainer = this.querySelector('.art-set-mock__frames');
      this.backgroundImg = this.querySelector('.art-set-mock__background');
      this.lightingImg = this.querySelector('.art-set-mock__lighting');

      if (!this.framesContainer) {
        console.error('[ArtSetMock] Frames container not found');
        return;
      }

      // Wait for background image to load to get accurate dimensions
      if (this.backgroundImg) {
        if (this.backgroundImg.complete) {
          this.init();
        } else {
          this.backgroundImg.addEventListener('load', () => this.init(), { once: true });
        }
      } else {
        this.init();
      }
    } catch (error) {
      console.error('[ArtSetMock] Error initializing:', error);
    }
  }

  init() {
    // Initial render
    requestAnimationFrame(() => {
      this.updateImageBounds();
      this.renderFrames();
      this.updateMobileCrop();
    });

    // Set up resize observer
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isUpdating) return;
      requestAnimationFrame(() => this.handleResize());
    });
    this.resizeObserver.observe(this);

    // Listen for window resize (for mobile crop recalculation)
    this.boundHandleResize = () => this.handleResize();
    window.addEventListener('resize', this.boundHandleResize);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
  }

  handleResize() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      this.updateImageBounds();
      this.updateFrameTransforms();
      this.updateMobileCrop();
      this.updateLightingClipPath();
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Calculate the actual displayed image bounds within the container.
   * Handles both object-fit: contain (letterbox) and object-fit: cover (crop)
   * by reading the computed style of the background image.
   */
  updateImageBounds() {
    // Use offsetWidth/Height to get un-transformed layout dimensions.
    // getBoundingClientRect() includes CSS transforms (e.g. square crop scale),
    // which would cause frame positions to be calculated at the wrong scale.
    const containerWidth = this.offsetWidth;
    const containerHeight = this.offsetHeight || containerWidth / this.imgAspect;

    if (!containerWidth) return;

    const containerAspect = containerWidth / containerHeight;
    const objectFit = this.backgroundImg
      ? getComputedStyle(this.backgroundImg).objectFit
      : 'contain';

    let displayWidth, displayHeight, offsetX, offsetY;

    if (objectFit === 'cover') {
      // Image fills the container — the larger dimension is constrained,
      // the other dimension overflows (negative offset = cropped).
      if (this.imgAspect > containerAspect) {
        // Image wider than container: fill height, crop sides
        displayHeight = containerHeight;
        displayWidth = containerHeight * this.imgAspect;
        offsetX = (containerWidth - displayWidth) / 2;
        offsetY = 0;
      } else {
        // Image taller than container: fill width, crop top/bottom
        displayWidth = containerWidth;
        displayHeight = containerWidth / this.imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - displayHeight) / 2;
      }
    } else {
      // object-fit: contain — image letterboxed inside the container.
      if (this.imgAspect > containerAspect) {
        // Image wider: letterbox top/bottom
        displayWidth = containerWidth;
        displayHeight = containerWidth / this.imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - displayHeight) / 2;
      } else {
        // Image taller: letterbox left/right
        displayHeight = containerHeight;
        displayWidth = containerHeight * this.imgAspect;
        offsetX = (containerWidth - displayWidth) / 2;
        offsetY = 0;
      }
    }

    this.imageBounds = {
      x: offsetX,
      y: offsetY,
      width: displayWidth,
      height: displayHeight
    };
  }

  /**
   * Render artwork frames with perspective transforms.
   */
  renderFrames() {
    this.framesContainer.innerHTML = '';

    const positionIds = Object.keys(this.framePolygons);
    if (positionIds.length === 0) return;

    positionIds.forEach((positionId, index) => {
      const polygon = this.framePolygons[positionId];
      const artwork = this.artworks[index];

      if (!polygon?.corners || polygon.corners.length !== 4) return;
      if (!artwork?.url) return;

      // Create frame container
      const frameEl = document.createElement('div');
      frameEl.className = 'art-set-mock__frame';
      frameEl.dataset.positionId = positionId;

      // Create artwork image
      const artworkImg = document.createElement('img');
      artworkImg.src = artwork.url;
      artworkImg.alt = artwork.title || 'Artwork';
      artworkImg.className = 'art-set-mock__frame-artwork';
      artworkImg.loading = 'lazy';

      frameEl.appendChild(artworkImg);
      this.framesContainer.appendChild(frameEl);

      // Apply transform once image is loaded (for accurate dimensions)
      if (artworkImg.complete) {
        this.applyFrameTransform(frameEl, polygon);
      } else {
        artworkImg.addEventListener('load', () => {
          this.applyFrameTransform(frameEl, polygon);
        }, { once: true });
      }
    });
  }

  /**
   * Update transforms for existing frames (called on resize).
   */
  updateFrameTransforms() {
    const frames = this.framesContainer.querySelectorAll('.art-set-mock__frame');
    frames.forEach(frameEl => {
      const positionId = frameEl.dataset.positionId;
      const polygon = this.framePolygons[positionId];
      if (polygon) {
        this.applyFrameTransform(frameEl, polygon);
      }
    });
  }

  /**
   * Apply perspective transform to a frame element.
   */
  applyFrameTransform(frameEl, polygon) {
    const artworkImg = frameEl.querySelector('.art-set-mock__frame-artwork');
    if (!artworkImg || !this.imageBounds.width) return;

    // Convert normalized corners (0-1) to pixel coordinates
    const pixelCorners = polygon.corners.map(corner => ({
      x: this.imageBounds.x + corner.x * this.imageBounds.width,
      y: this.imageBounds.y + corner.y * this.imageBounds.height
    }));

    // Calculate artwork dimensions from polygon bounds
    const minX = Math.min(...pixelCorners.map(c => c.x));
    const maxX = Math.max(...pixelCorners.map(c => c.x));
    const minY = Math.min(...pixelCorners.map(c => c.y));
    const maxY = Math.max(...pixelCorners.map(c => c.y));

    const artworkWidth = maxX - minX;
    const artworkHeight = maxY - minY;

    // Calculate matrix3d transform
    const transform = this.calculateMatrix3d(pixelCorners, artworkWidth, artworkHeight);

    // Apply styles
    artworkImg.style.width = `${artworkWidth}px`;
    artworkImg.style.height = `${artworkHeight}px`;
    artworkImg.style.transform = transform;

    // Set frame container clip-path for clean edges
    // Use offsetWidth/Height (un-transformed) for consistent clip-path percentages
    const clipWidth = this.offsetWidth;
    const clipHeight = this.offsetHeight;
    const clipPoints = pixelCorners.map(c => {
      const percentX = ((c.x) / clipWidth * 100).toFixed(2);
      const percentY = ((c.y) / clipHeight * 100).toFixed(2);
      return `${percentX}% ${percentY}%`;
    });
    frameEl.style.clipPath = `polygon(${clipPoints.join(', ')})`;
    frameEl.style.width = '100%';
    frameEl.style.height = '100%';
  }

  /**
   * Update lighting overlay clip-path to only show lighting within frame areas.
   */
  updateLightingClipPath() {
    if (!this.lightingImg || this.lightingOpacity <= 0) return;

    const clipPaths = [];
    const positionIds = Object.keys(this.framePolygons);

    positionIds.forEach(positionId => {
      const polygon = this.framePolygons[positionId];
      if (!polygon?.corners || polygon.corners.length !== 4) return;

      // Convert normalized corners to percentage of container (un-transformed)
      const lw = this.offsetWidth;
      const lh = this.offsetHeight;
      const points = polygon.corners.map(corner => {
        const pixelX = this.imageBounds.x + corner.x * this.imageBounds.width;
        const pixelY = this.imageBounds.y + corner.y * this.imageBounds.height;
        const percentX = (pixelX / lw * 100).toFixed(2);
        const percentY = (pixelY / lh * 100).toFixed(2);
        return `${percentX}% ${percentY}%`;
      });
      clipPaths.push(`polygon(${points.join(', ')})`);
    });

    // Combine all frame polygons (note: this creates a simple union for single frames)
    // For multiple frames, we'd need a more complex approach
    if (clipPaths.length > 0) {
      this.lightingImg.style.clipPath = clipPaths[0];
    }
  }

  /**
   * Update mobile square crop transform.
   */
  updateMobileCrop() {
    if (!this.cropWrapper || !this.isMobileCrop || !this.squareCrop) return;

    // Only apply on mobile viewports
    const isMobile = window.innerWidth < 750;
    if (!isMobile) {
      this.cropWrapper.style.transform = '';
      return;
    }

    const { centerX, centerY, size } = this.squareCrop;
    if (!size) {
      this.cropWrapper.style.transform = '';
      return;
    }

    // Calculate crop transform accounting for letterboxing
    let scale, cropLeft, cropTop;

    if (this.imgAspect >= 1) {
      // Wide image (e.g., 16:9)
      const displayedHeightRatio = 1 / this.imgAspect;
      const yOffset = (1 - displayedHeightRatio) / 2;

      scale = 1 / (size * displayedHeightRatio);
      const cropWidthNorm = size / this.imgAspect;
      cropLeft = centerX - cropWidthNorm / 2;
      cropTop = yOffset + (centerY - size / 2) * displayedHeightRatio;
    } else {
      // Tall image
      const displayedWidthRatio = this.imgAspect;
      const xOffset = (1 - displayedWidthRatio) / 2;

      scale = 1 / (size * displayedWidthRatio);
      const cropHeightNorm = size / this.imgAspect;
      cropLeft = xOffset + (centerX - size / 2) * displayedWidthRatio;
      cropTop = centerY - cropHeightNorm / 2;
    }

    const translateX = -cropLeft * 100;
    const translateY = -cropTop * 100;

    this.cropWrapper.style.transform = `scale(${scale}) translate(${translateX}%, ${translateY}%)`;
  }

  /**
   * Calculate CSS matrix3d transform from 4 corner points.
   * Maps a rectangle (artwork) to a quadrilateral (perspective area).
   *
   * @param {Array} corners - Array of 4 points {x, y} in pixel coordinates (TL, TR, BR, BL)
   * @param {number} srcWidth - Source image width
   * @param {number} srcHeight - Source image height
   * @returns {string} CSS matrix3d() transform value
   */
  calculateMatrix3d(corners, srcWidth, srcHeight) {
    if (!corners || corners.length !== 4 || !srcWidth || !srcHeight) {
      return 'none';
    }

    // Source corners (original image bounds)
    const src = [
      { x: 0, y: 0 },              // top-left
      { x: srcWidth, y: 0 },       // top-right
      { x: srcWidth, y: srcHeight }, // bottom-right
      { x: 0, y: srcHeight }       // bottom-left
    ];

    // Destination corners (from frame polygon)
    const dst = corners;

    try {
      const H = this.computeHomography(src, dst);

      // Convert to CSS matrix3d format (column-major)
      const matrix3d = [
        H[0][0], H[1][0], 0, H[2][0],
        H[0][1], H[1][1], 0, H[2][1],
        0, 0, 1, 0,
        H[0][2], H[1][2], 0, H[2][2]
      ];

      return `matrix3d(${matrix3d.join(', ')})`;
    } catch (e) {
      console.error('[ArtSetMock] Error calculating matrix3d:', e);
      return 'none';
    }
  }

  /**
   * Compute homography matrix from source to destination points.
   * Uses Gaussian elimination to solve the system of equations.
   */
  computeHomography(src, dst) {
    // Build the system of equations: Ah = b
    const A = [];
    const b = [];

    for (let i = 0; i < 4; i++) {
      const sx = src[i].x;
      const sy = src[i].y;
      const dx = dst[i].x;
      const dy = dst[i].y;

      A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
      b.push(dx);
      A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
      b.push(dy);
    }

    // Solve using Gaussian elimination
    const h = this.gaussianElimination(A, b);

    // Build 3x3 homography matrix
    return [
      [h[0], h[1], h[2]],
      [h[3], h[4], h[5]],
      [h[6], h[7], 1]
    ];
  }

  /**
   * Gaussian elimination with partial pivoting.
   */
  gaussianElimination(A, b) {
    const n = A.length;
    const augmented = A.map((row, i) => [...row, b[i]]);
    const EPSILON = 1e-10;

    // Forward elimination with partial pivoting
    for (let i = 0; i < n; i++) {
      // Find pivot
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
          maxRow = k;
        }
      }
      [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

      // Check for singular matrix (pivot is effectively zero)
      if (Math.abs(augmented[i][i]) < EPSILON) {
        console.warn('[ArtSetMock] Near-singular matrix in Gaussian elimination');
        // Return identity-like solution to avoid NaN
        return new Array(n).fill(0);
      }

      // Make all rows below this one 0 in current column
      for (let k = i + 1; k < n; k++) {
        const c = augmented[k][i] / augmented[i][i];
        for (let j = i; j <= n; j++) {
          if (i === j) {
            augmented[k][j] = 0;
          } else {
            augmented[k][j] -= c * augmented[i][j];
          }
        }
      }
    }

    // Back substitution
    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
      if (Math.abs(augmented[i][i]) < EPSILON) {
        x[i] = 0;
        continue;
      }
      x[i] = augmented[i][n] / augmented[i][i];
      for (let k = i - 1; k >= 0; k--) {
        augmented[k][n] -= augmented[k][i] * x[i];
      }
    }

    return x;
  }
}

// Register the custom element
if (!customElements.get('art-set-mock')) {
  customElements.define('art-set-mock', ArtSetMock);
}
